import React, { useRef, useEffect, useState } from 'react';

interface RangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
}

const RangeSlider: React.FC<RangeSliderProps> = ({ min, max, value, onChange }) => {
  const [minVal, setMinVal] = useState(value[0]);
  const [maxVal, setMaxVal] = useState(value[1]);
  const minValRef = useRef<number>(value[0]);
  const maxValRef = useRef<number>(value[1]);
  const range = useRef<HTMLDivElement>(null);

  const getPercent = (value: number) =>
    Math.round(((value - min) / (max - min)) * 100);

  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxValRef.current);

    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minVal, min, max]);

  useEffect(() => {
    const minPercent = getPercent(minValRef.current);
    const maxPercent = getPercent(maxVal);

    if (range.current) {
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [maxVal, min, max]);

  useEffect(() => {
    setMinVal(value[0]);
    setMaxVal(value[1]);
    minValRef.current = value[0];
    maxValRef.current = value[1];
  }, [value]);

  return (
    <div className="relative pt-2 pb-2">
      {/* Current values displayed above the handles */}
      <div
        className="absolute bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium"
        style={{ left: `calc(${getPercent(minVal)}% - 20px)`, top: '-28px' }}
      >
        {minVal}
      </div>
      <div
        className="absolute bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium"
        style={{ left: `calc(${getPercent(maxVal)}% - 20px)`, top: '-28px' }}
      >
        {maxVal}
      </div>

      <input
        type="range"
        min={min}
        max={max}
        value={minVal}
        onChange={(event) => {
          const value = Math.min(Number(event.target.value), maxVal - 1);
          setMinVal(value);
          minValRef.current = value;
          onChange([value, maxVal]);
        }}
        className="thumb thumb--left"
        style={{ zIndex: minVal > max - 100 ? 5 : 3 }}
      />
      <input
        type="range"
        min={min}
        max={max}
        value={maxVal}
        onChange={(event) => {
          const value = Math.max(Number(event.target.value), minVal + 1);
          setMaxVal(value);
          maxValRef.current = value;
          onChange([minVal, value]);
        }}
        className="thumb thumb--right"
      />

      <div className="slider">
        <div className="slider__track" />
        <div ref={range} className="slider__range" />
      </div>

      <style>{`
        .thumb {
          pointer-events: none;
          position: absolute;
          height: 0;
          width: 100%;
          outline: none;
        }

        .thumb--left {
          z-index: 3;
        }

        .thumb--right {
          z-index: 4;
        }

        .thumb::-webkit-slider-thumb {
          background-color: #f43f5e;
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
          height: 18px;
          width: 18px;
          margin-top: 4px;
          pointer-events: all;
          position: relative;
          -webkit-appearance: none;
        }

        .thumb::-moz-range-thumb {
          background-color: #f43f5e;
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
          height: 18px;
          width: 18px;
          pointer-events: all;
          position: relative;
        }

        .slider {
          position: relative;
          width: 100%;
        }

        .slider__track {
          background-color: #e5e7eb;
          border-radius: 3px;
          height: 5px;
          position: absolute;
          width: 100%;
        }

        .slider__range {
          background-color: #f43f5e;
          border-radius: 3px;
          height: 5px;
          position: absolute;
        }
      `}</style>
    </div>
  );
};

export default RangeSlider;
